import moment from 'moment';
import { Device, DevicePlan, DeviceSubscription } from '../hooks/useGetCurrentUserDevices.js';

export const DEFAULT_DEVICE_ACTIVITY_STATE_COLOR = '#979797'; // grey
const DEVICE_ACTIVITY_STATE_COLORS = {
  'inactive': DEFAULT_DEVICE_ACTIVITY_STATE_COLOR,
  'moving': '#39DD76',  // Green
  'stopped': '#ff3e3e', // Red
  'sleeping': 'orange', // Orange
}

export const DEFAULT_DEVICE_ONLINE_STATE_COLOR = '#979797'; // grey
const DEVICE_ONLINE_STATE_COLORS = {
  'online': '#39DD76',  // Green
  'offline': '#979797', // Grey
  'sleeping': 'orange', // Orange
}


export type DeviceActivityState = 'inactive' | 'moving' | 'stopped' | 'sleeping';

/**
 * 
 * @param batteryMode 
 * @returns 
 */
export const isBatteryModeFixedInterval = (batteryMode: string | undefined): boolean => {
  if (!batteryMode) {
    return false;
  }

  return batteryMode?.includes('fixed-interval');
};

/**
 * 
 * @param batteryMode 
 * @returns 
 */
export const getBatteryModeFixedIntervalMinutes = (batteryMode: string | undefined): number => {
  if (!batteryMode || !isBatteryModeFixedInterval(batteryMode)) {
    return 0;
  }

  // Split batteryMode string by '-', select 3rd part, convert to minutes which requires
  // parsing the 'hr' suffix and multiplying by 60.
  return parseInt(batteryMode.split('-')[2].replace('hr', '')) * 60;
}

/**
 * States are: 
 *  - Moving - Device has been seen very recently (10 mins) and speed > 0
 *  - Stopped - Device has been seen recently (30 mins) but isn't moving
 *  - Sleeping - Device is in a low power mode and hasn't been seen recently
 *  - Inactive - Device hasn't been seen recently (exact threshold depends if device is in low-power mode)
 * 
 * @param device 
 * @returns 
 */
export const deviceActivityState = (device: Device): DeviceActivityState => {
  // If position_at > 30.mins.ago -- inactive
  // If speed > 0 & position_at < 10.minutes -- moving
  // else, stopped

  const devicePosition = device.lastPosition || device.position;
  // device.position could be null, or device.lastseenAt could be undefined (when in history mode)
  const lastSeenAtMinutes = device.lastseenAt ? moment().diff(device.lastseenAt, 'minutes') : 0;
  if (isNaN(lastSeenAtMinutes) || !devicePosition) {
    return 'inactive';
  }

  if(lastSeenAtMinutes >= 30) {
    // Normally this indicates offline... but if the device is in low-power mode, it could be sleeping.
    if(isBatteryModeFixedInterval(device.batteryMode)) {
      const interval = getBatteryModeFixedIntervalMinutes(device.batteryMode);

      if(lastSeenAtMinutes < (interval * 2) + 30) {
        return 'sleeping';
      }
    }

    return 'inactive';
  }

  if (Number(devicePosition.speed) > 0 && lastSeenAtMinutes < 10) {
    return 'moving';
  }

  return 'stopped';
};

export const deviceActivityStateColor = (device: Device): string => {
  const status = deviceActivityState(device);
  return DEVICE_ACTIVITY_STATE_COLORS[status] || DEFAULT_DEVICE_ACTIVITY_STATE_COLOR;
}


export type DeviceOnlineState = 'online' | 'offline' | 'sleeping';


/**
 * Online - server reports isOnline (heartbeat within 30 mins)
 * Sleeping - Not online but in low-power mode and has been seen within threshold.
 * Offline - anything else. 
 * 
 * @param device 
 * @returns 
 */
export const deviceOnlineState = (device: Device): DeviceOnlineState => {
  // If position_at > 30.mins.ago -- inactive
  // If speed > 0 & position_at < 10.minutes -- moving
  // else, stopped

  if (device.isOnline) {
    return 'online';
  }

  const lastSeenAtMinutes = device.lastseenAt ? moment().diff(device.lastseenAt, 'minutes') : 0;

  if(lastSeenAtMinutes >= 30) {
    // Normally this indicates offline... but if the device is in low-power mode, it could be sleeping.
    if(isBatteryModeFixedInterval(device.batteryMode)) {
      const interval = getBatteryModeFixedIntervalMinutes(device.batteryMode);

      if(lastSeenAtMinutes < (interval * 2) + 30) {
        return 'sleeping';
      }
    }
  }

  return 'offline';
};

export const deviceOnlineStateColor = (device: Device): string => {
  const state = deviceOnlineState(device);

  return DEVICE_ONLINE_STATE_COLORS[state] || DEFAULT_DEVICE_ONLINE_STATE_COLOR;
}

/**
 * Describe a device subscription.
 * 
 * @param plan 
 * @returns 
 */
export const describeDeviceService = (subscription: DeviceSubscription): string => {
  const plan = subscription.plan;

  let descriptionParts = [
    `${plan.name}`,
    '/',
  ]

  if (subscription.isPrepaid) {
    if (subscription.prepaidPeriodDays) {
      descriptionParts.push(`${prepaidDurationDescription(subscription.prepaidPeriodDays)} free`);
    } else {
      descriptionParts.push('Prepaid');
    }
  } else {
    descriptionParts.push(`${plan.price} ${plan.currency} ${plan.interval}`);
  }

  return descriptionParts.join(' ');
}


/**
 * Describe a device plan.
 * 
 * @param plan 
 * @returns 
 */
export const describePlan = (plan: DevicePlan): string => {
  let descriptionParts = [
    `${plan.name}`,
    '/',
    `${plan.price} ${plan.currency} ${plan.interval}`,
  ];

  return descriptionParts.join(' ');
}

/**
 * Describe the prepaid duration.
 * 
 * @param durationDays 
 * @returns 
 */
const prepaidDurationDescription = (durationDays: number): string => {
  // 20Sep24: Just convert days to number of months rounded. That should do fine.
  // 365 -> 12 months
  // 60 -> 2 months
  // 5 -> 5 days

  if (durationDays < 30) {
    return `${durationDays} days`;
  }

  const numMonths = Math.floor(durationDays / 30);
  return numMonths === 1 ? '1 month' : `${numMonths} months`;
}